/**
 * FolderTree - Árbol de listas estilo ToDoList
 * Renombrado de "Carpetas" a "Listas" en v1.1
 * Con secciones desplegables, color picker, edición inline y más
 */

import { useState, useRef, useEffect } from 'react'

// Paleta de colores disponibles
const COLOR_PALETTE = [
  { name: 'Gris', value: '#6B7280' },
  { name: 'Rojo', value: '#EF4444' },
  { name: 'Naranja', value: '#F97316' },
  { name: 'Amarillo', value: '#EAB308' },
  { name: 'Verde', value: '#22C55E' },
  { name: 'Esmeralda', value: '#10B981' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Violeta', value: '#8B5CF6' },
  { name: 'Rosa', value: '#EC4899' },
  { name: 'Fucsia', value: '#D946EF' },
  { name: 'Marrón', value: '#A16207' },
]

// Emojis disponibles para listas
const EMOJI_PALETTE = [
  '📁', '📂', '📋', '📝', '📌', '⭐', '🔥', '💎', '🎯', '🚀',
  '💼', '🏷️', '📦', '🎁', '💡', '🔔', '❤️', '💚', '💙', '💜',
  '🟢', '🔵', '🟣', '🟠', '🔴', '⚡', '✨', '🌟', '🎨', '🛒',
  '📞', '💬', '📧', '🏠', '🏢', '👤', '👥', '🤝', '💰', '📈',
]

export default function FolderTree({
  folders,
  selectedFolder,
  onSelectFolder,
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  onDropContact,
  onReorderFolders,
  // Props para filtros de Wassenger
  labels = [],
  departments = [],
  selectedLabel,
  selectedDepartment,
  onSelectLabel,
  onSelectDepartment
}) {
  const [expandedFolders, setExpandedFolders] = useState(new Set())
  const [newFolderName, setNewFolderName] = useState('')
  const [showNewFolderInput, setShowNewFolderInput] = useState(false)
  const [dragOverFolderId, setDragOverFolderId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  // Refs para reordenamiento (no usar useState - causa stale closures en drag events)
  const dragItemIndex = useRef(null)
  const dragOverIndex = useRef(null)
  const [draggingFolderId, setDraggingFolderId] = useState(null)
  const [dropTarget, setDropTarget] = useState(null) // { folderId, position: 'before' | 'after' }

  // Estados para edición
  const [editingFolderId, setEditingFolderId] = useState(null)
  const [editingField, setEditingField] = useState(null) // 'name', 'color', 'emoji'
  const [editName, setEditName] = useState('')

  // Refs
  const editInputRef = useRef(null)
  const colorPickerRef = useRef(null)
  const emojiPickerRef = useRef(null)

  // Estado para secciones expandidas
  const [expandedSections, setExpandedSections] = useState({
    agents: true,
    folders: true,
    labels: false,
    departments: false
  })

  // Cerrar pickers al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        if (editingField === 'color') setEditingField(null)
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        if (editingField === 'emoji') setEditingField(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [editingField])

  // Focus en input al editar nombre
  useEffect(() => {
    if (editingField === 'name' && editInputRef.current) {
      editInputRef.current.focus()
      editInputRef.current.select()
    }
  }, [editingField])

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Separar listas de sistema y personalizadas
  const systemFolders = folders.filter(f => f.es_sistema)
  const customFolders = folders.filter(f => !f.es_sistema && (showArchived || !f.archivada))
  const archivedCount = folders.filter(f => !f.es_sistema && f.archivada).length

  // Filtrar por búsqueda y ordenar por campo 'orden'
  const filteredCustomFolders = customFolders
    .filter(f => !searchQuery || f.nombre.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => (a.orden || 0) - (b.orden || 0))

  // Ref sincronizada para evitar stale closures en drag handlers
  const filteredFoldersRef = useRef(filteredCustomFolders)
  filteredFoldersRef.current = filteredCustomFolders

  const toggleExpand = (folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      onCreateFolder(newFolderName.trim())
      setNewFolderName('')
      setShowNewFolderInput(false)
    }
  }

  // Iniciar edición de nombre
  const startEditingName = (folder, e) => {
    e.stopPropagation()
    setEditingFolderId(folder.id)
    setEditingField('name')
    setEditName(folder.nombre)
  }

  // Guardar nombre editado
  const saveEditedName = () => {
    if (editName.trim() && editName !== folders.find(f => f.id === editingFolderId)?.nombre) {
      onUpdateFolder(editingFolderId, { nombre: editName.trim() })
    }
    setEditingFolderId(null)
    setEditingField(null)
  }

  // Cambiar color
  const handleColorChange = (folderId, color) => {
    onUpdateFolder(folderId, { color })
    setEditingField(null)
    setEditingFolderId(null)
  }

  // Cambiar emoji
  const handleEmojiChange = (folderId, icono) => {
    onUpdateFolder(folderId, { icono })
    setEditingField(null)
    setEditingFolderId(null)
  }

  // Archivar/Desarchivar
  const handleToggleArchive = (folder, e) => {
    e.stopPropagation()
    onUpdateFolder(folder.id, { archivada: !folder.archivada })
  }

  const handleDragOver = (e, folderId) => {
    e.preventDefault()
    setDragOverFolderId(folderId)
  }

  const handleDragLeave = () => {
    setDragOverFolderId(null)
  }

  const handleDrop = (e, folder) => {
    e.preventDefault()
    setDragOverFolderId(null)

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'))
      if (data.contactId) {
        onDropContact(data.contactId, folder.id, data.sourceFolderId)
      }
    } catch (error) {
      console.error('Error en drop:', error)
    }
  }

  // === Handlers para reordenamiento de listas (patrón useRef) ===
  const handleFolderDragStart = (e, folder, index) => {
    if (folder.es_sistema) return
    e.stopPropagation()
    dragItemIndex.current = index
    setDraggingFolderId(folder.id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', folder.id.toString())
  }

  const handleFolderDragEnter = (e, folder, index) => {
    if (dragItemIndex.current === null || folder.es_sistema) return
    e.preventDefault()

    dragOverIndex.current = index

    // Visual: calcular si es "antes" o "después" para la línea
    const rect = e.currentTarget.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const position = e.clientY < midY ? 'before' : 'after'
    setDropTarget({ folderId: folder.id, position })
  }

  const handleFolderDragOver = (e, folder) => {
    if (dragItemIndex.current === null || folder.es_sistema) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    // Actualizar posición visual (antes/después)
    const rect = e.currentTarget.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    const position = e.clientY < midY ? 'before' : 'after'
    setDropTarget(prev => {
      if (prev?.folderId === folder.id && prev?.position === position) return prev
      return { folderId: folder.id, position }
    })
  }

  const handleFolderDragEnd = () => {
    // Ejecutar reorden usando refs (siempre tienen valor actual, sin stale closures)
    if (dragItemIndex.current !== null && dragOverIndex.current !== null
      && dragItemIndex.current !== dragOverIndex.current && onReorderFolders) {

      const currentList = filteredFoldersRef.current
      const newList = [...currentList]
      const [draggedItem] = newList.splice(dragItemIndex.current, 1)
      newList.splice(dragOverIndex.current, 0, draggedItem)

      const orderedIds = newList.map(f => f.id)
      onReorderFolders(orderedIds)
    }

    // Reset
    dragItemIndex.current = null
    dragOverIndex.current = null
    setDraggingFolderId(null)
    setDropTarget(null)
  }

  const renderFolder = (folder, level = 0, listIndex = null) => {
    const isSelected = selectedFolder?.id === folder.id
    const isExpanded = expandedFolders.has(folder.id)
    const isDragOverForContact = dragOverFolderId === folder.id && !draggingFolderId
    const isEditing = editingFolderId === folder.id
    const childFolders = folders.filter(f => f.parent_id === folder.id)
    const hasChildren = childFolders.length > 0
    const isDragging = draggingFolderId === folder.id
    const isDropBefore = dropTarget?.folderId === folder.id && dropTarget?.position === 'before'
    const isDropAfter = dropTarget?.folderId === folder.id && dropTarget?.position === 'after'

    return (
      <div key={folder.id} className="group relative">
        {/* Indicador de drop "antes" - línea azul gruesa */}
        {isDropBefore && (
          <div className="absolute top-0 left-2 right-2 h-1 bg-primary-500 rounded-full z-20 shadow-sm" />
        )}
        <div
          className={`
            relative flex items-center gap-2 px-3 py-2 cursor-pointer transition-all duration-150
            ${isSelected ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300' : 'hover:bg-gray-100 dark:hover:bg-surface-dark-hover dark:text-gray-300'}
            ${isDragOverForContact ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-400 dark:border-blue-600 border-dashed' : ''}
            ${folder.archivada ? 'opacity-60' : ''}
            ${isDragging ? 'opacity-40 scale-95' : ''}
            ${!folder.es_sistema ? 'cursor-grab active:cursor-grabbing' : ''}
          `}
          style={{ paddingLeft: `${12 + level * 16}px` }}
          draggable={!folder.es_sistema}
          onClick={() => onSelectFolder(folder)}
          onDragStart={(e) => handleFolderDragStart(e, folder, listIndex)}
          onDragEnter={(e) => handleFolderDragEnter(e, folder, listIndex)}
          onDragEnd={handleFolderDragEnd}
          onDragOver={(e) => {
            if (draggingFolderId) {
              handleFolderDragOver(e, folder)
            } else {
              handleDragOver(e, folder.id)
            }
          }}
          onDragLeave={(e) => {
            if (!draggingFolderId) {
              handleDragLeave()
            }
          }}
          onDrop={(e) => {
            if (!draggingFolderId) {
              handleDrop(e, folder)
            }
          }}
        >
          {/* Icono expandir/colapsar */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand(folder.id)
              }}
              className="w-4 h-4 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          ) : (
            <span className="w-4"></span>
          )}

          {/* Emoji/Icono de la lista - Solo para listas personalizadas */}
          {!folder.es_sistema && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setEditingFolderId(folder.id)
                setEditingField('emoji')
              }}
              className="text-base transition-transform hover:scale-125"
              title="Cambiar icono"
            >
              {folder.icono || '📋'}
            </button>
          )}

          {/* Indicador de color - Solo para listas personalizadas */}
          <span
            onClick={(e) => {
              e.stopPropagation()
              if (!folder.es_sistema) {
                setEditingFolderId(folder.id)
                setEditingField('color')
              }
            }}
            className={`w-3 h-3 rounded-full flex-shrink-0 ${!folder.es_sistema ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-400' : ''}`}
            style={{ backgroundColor: folder.color || '#6B7280' }}
            title={folder.es_sistema ? '' : 'Cambiar color'}
          />

          {/* Nombre (editable) */}
          {isEditing && editingField === 'name' ? (
            <input
              ref={editInputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveEditedName()
                if (e.key === 'Escape') {
                  setEditingFolderId(null)
                  setEditingField(null)
                }
              }}
              onBlur={saveEditedName}
              className="flex-1 px-1 py-0.5 text-sm font-medium border border-primary-300 dark:border-primary-700 rounded dark:bg-surface-dark dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className="flex-1 truncate text-sm font-medium"
              onDoubleClick={(e) => !folder.es_sistema && startEditingName(folder, e)}
              title={folder.descripcion || folder.nombre}
            >
              {folder.nombre}
              {folder.archivada && <span className="ml-1 text-gray-400 dark:text-gray-500">(archivada)</span>}
            </span>
          )}

          {/* Contenedor derecho con contador y acciones */}
          <div className="flex items-center gap-1 ml-auto">
            {/* Contador de vencidos - siempre visible */}
            {folder.overdue_count > 0 && (
              <span className="text-[10px] text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full font-medium" title="Contactos vencidos">
                {folder.overdue_count}
              </span>
            )}

            {/* Contador total - visible cuando NO hay hover en listas personalizadas */}
            <span className={`
              text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-surface-dark-hover px-1.5 py-0.5 rounded transition-all duration-200
              ${!folder.es_sistema ? 'group-hover:opacity-0 group-hover:w-0 group-hover:px-0 group-hover:overflow-hidden' : ''}
            `}>
              {folder.contact_count || 0}
            </span>

            {/* Menú de acciones (solo listas personalizadas) - visible en hover */}
            {!folder.es_sistema && (
              <div className="flex items-center overflow-hidden transition-all duration-200 ease-out max-w-0 opacity-0 group-hover:max-w-[100px] group-hover:opacity-100">
                <button
                  onClick={(e) => startEditingName(folder, e)}
                  className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded transition-colors"
                  title="Editar nombre"
                >
                  ✏️
                </button>
                <button
                  onClick={(e) => handleToggleArchive(folder, e)}
                  className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors"
                  title={folder.archivada ? 'Desarchivar' : 'Archivar'}
                >
                  {folder.archivada ? '📤' : '📥'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(`¿Eliminar la lista "${folder.nombre}"?\nLos contactos no se eliminarán, solo se quitarán de esta lista.`)) {
                      onDeleteFolder(folder.id)
                    }
                  }}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                  title="Eliminar lista"
                >
                  🗑️
                </button>
              </div>
            )}
          </div>

          {/* Color Picker Modal */}
          {isEditing && editingField === 'color' && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/20 dark:bg-black/50 z-[99]"
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingField(null)
                  setEditingFolderId(null)
                }}
              />
              <div
                ref={colorPickerRef}
                className="fixed bg-white dark:bg-surface-dark-alt rounded-lg shadow-2xl border border-gray-200 dark:border-surface-dark-border p-3 z-[100]"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Seleccionar color</div>
                <div className="grid grid-cols-4 gap-2">
                  {COLOR_PALETTE.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => handleColorChange(folder.id, color.value)}
                      className={`w-8 h-8 rounded-full hover:scale-110 transition-transform ${
                        folder.color === color.value ? 'ring-2 ring-offset-2 ring-primary-500' : ''
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Emoji Picker Modal */}
          {isEditing && editingField === 'emoji' && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/20 dark:bg-black/50 z-[99]"
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingField(null)
                  setEditingFolderId(null)
                }}
              />
              <div
                ref={emojiPickerRef}
                className="fixed bg-white dark:bg-surface-dark-alt rounded-lg shadow-2xl border border-gray-200 dark:border-surface-dark-border p-3 z-[100] w-72"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Seleccionar icono</div>
                <div className="grid grid-cols-8 gap-1">
                  {EMOJI_PALETTE.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleEmojiChange(folder.id, emoji)}
                      className={`w-8 h-8 text-xl flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-surface-dark-hover transition ${
                        folder.icono === emoji ? 'bg-primary-100 dark:bg-primary-900/30 ring-2 ring-primary-500' : ''
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Indicador de drop "después" - línea azul gruesa */}
        {isDropAfter && (
          <div className="absolute bottom-0 left-2 right-2 h-1 bg-primary-500 rounded-full z-20 shadow-sm" />
        )}

        {/* Hijos */}
        {isExpanded && hasChildren && (
          <div>
            {childFolders.map(child => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Opción "Todos" - Estilo Koomo */}
      <div
        className={`
          flex items-center gap-3 px-4 py-3 cursor-pointer transition-all duration-150 mx-2 my-1 rounded-xl
          ${!selectedFolder && !selectedLabel && !selectedDepartment
            ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
            : 'bg-white dark:bg-surface-dark-alt hover:bg-gray-50 dark:hover:bg-surface-dark-hover shadow-sm border border-gray-100 dark:border-surface-dark-border dark:text-gray-300'}
        `}
        onClick={() => {
          onSelectFolder(null)
          onSelectLabel?.(null)
          onSelectDepartment?.(null)
        }}
      >
        <span className="text-lg">📋</span>
        <span className="flex-1 text-sm font-semibold">Todos los contactos</span>
      </div>

      {/* Sección: Por Agente */}
      <div
        className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100/80 dark:bg-surface-dark/80 flex items-center justify-between cursor-pointer hover:bg-gray-200/80 dark:hover:bg-surface-dark-hover mt-2"
        onClick={() => toggleSection('agents')}
      >
        <span>Por Agente</span>
        <span className="text-gray-400">{expandedSections.agents ? '▼' : '▶'}</span>
      </div>
      {expandedSections.agents && systemFolders.map(folder => renderFolder(folder))}

      {/* Sección: Mis Listas */}
      <div
        className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100/80 dark:bg-surface-dark/80 flex items-center justify-between cursor-pointer hover:bg-gray-200/80 dark:hover:bg-surface-dark-hover"
        onClick={() => toggleSection('folders')}
      >
        <span>Mis Listas</span>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowNewFolderInput(true)
              setExpandedSections(prev => ({ ...prev, folders: true }))
            }}
            className="w-5 h-5 flex items-center justify-center bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800/40 rounded-md text-sm font-bold transition"
            title="Nueva lista"
          >
            +
          </button>
          <span className="text-gray-400">{expandedSections.folders ? '▼' : '▶'}</span>
        </div>
      </div>

      {expandedSections.folders && (
        <>
          {/* Barra de búsqueda */}
          {customFolders.length > 5 && (
            <div className="px-3 py-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar lista..."
                className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-surface-dark-border rounded-lg dark:bg-surface-dark dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {/* Input nueva lista */}
          {showNewFolderInput && (
            <div className="px-3 py-2 flex gap-2 mx-2">
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFolder()
                  if (e.key === 'Escape') setShowNewFolderInput(false)
                }}
                placeholder="Nombre de lista..."
                className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-surface-dark-border rounded dark:bg-surface-dark dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                autoFocus
              />
              <button
                onClick={handleCreateFolder}
                className="px-2 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 dark:hover:bg-primary-500"
              >
                OK
              </button>
            </div>
          )}

          {/* Listas personalizadas */}
          {filteredCustomFolders.length === 0 && !showNewFolderInput && (
            <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 text-center">
              {searchQuery ? 'No se encontraron listas' : 'No hay listas personalizadas'}
            </div>
          )}
          {filteredCustomFolders.map((folder, index) => renderFolder(folder, 0, index))}

          {/* Toggle mostrar archivadas */}
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="w-full px-3 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-hover flex items-center justify-center gap-1"
            >
              {showArchived ? '🔼 Ocultar archivadas' : `📦 Ver archivadas (${archivedCount})`}
            </button>
          )}
        </>
      )}

      {/* Sección: Etiquetas de Wassenger - Estilo Koomo */}
      <div
        className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100/80 dark:bg-surface-dark/80 flex items-center justify-between cursor-pointer hover:bg-gray-200/80 dark:hover:bg-surface-dark-hover"
        onClick={() => toggleSection('labels')}
      >
        <span>🏷️ Etiquetas</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-surface-dark-hover dark:text-gray-500 px-1.5 py-0.5 rounded-full">{labels.length}</span>
          <span className="text-gray-400">{expandedSections.labels ? '▼' : '▶'}</span>
        </div>
      </div>
      {expandedSections.labels && (
        <div className="max-h-72 overflow-y-auto px-2 py-1">
          {labels.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-400 text-center bg-white dark:bg-surface-dark-alt rounded-lg mx-1">
              No hay etiquetas
            </div>
          ) : (
            labels.map(label => (
              <div
                key={label.id}
                className={`
                  flex items-center gap-2 px-3 py-2 cursor-pointer transition-all duration-150 rounded-lg my-0.5
                  ${selectedLabel?.id === label.id
                    ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'hover:bg-white dark:hover:bg-surface-dark-hover hover:shadow-sm dark:text-gray-300'}
                `}
                onClick={() => {
                  onSelectLabel?.(selectedLabel?.id === label.id ? null : label)
                  onSelectFolder(null)
                }}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm"
                  style={{ backgroundColor: label.color || '#6B7280' }}
                />
                <span className="flex-1 text-sm truncate">{label.name}</span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-hover px-1.5 py-0.5 rounded-full">
                  {label.contact_count || 0}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Sección: Departamentos de Wassenger - Estilo Koomo */}
      <div
        className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-100/80 dark:bg-surface-dark/80 flex items-center justify-between cursor-pointer hover:bg-gray-200/80 dark:hover:bg-surface-dark-hover"
        onClick={() => toggleSection('departments')}
      >
        <span>🏢 Departamentos</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 bg-gray-200 dark:bg-surface-dark-hover dark:text-gray-500 px-1.5 py-0.5 rounded-full">{departments.length}</span>
          <span className="text-gray-400">{expandedSections.departments ? '▼' : '▶'}</span>
        </div>
      </div>
      {expandedSections.departments && (
        <div className="max-h-72 overflow-y-auto px-2 py-1">
          {departments.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-400 text-center bg-white dark:bg-surface-dark-alt rounded-lg mx-1">
              No hay departamentos
            </div>
          ) : (
            departments.map(dept => (
              <div
                key={dept.id}
                className={`
                  flex items-center gap-2 px-3 py-2 cursor-pointer transition-all duration-150 rounded-lg my-0.5
                  ${selectedDepartment?.id === dept.id
                    ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'hover:bg-white dark:hover:bg-surface-dark-hover hover:shadow-sm dark:text-gray-300'}
                `}
                onClick={() => {
                  onSelectDepartment?.(selectedDepartment?.id === dept.id ? null : dept)
                  onSelectFolder(null)
                }}
              >
                <span className="w-4 text-center">🏢</span>
                <span className="flex-1 text-sm truncate">{dept.name}</span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-surface-dark-hover px-1.5 py-0.5 rounded-full">
                  {dept.contact_count || 0}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
